import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Group } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { groupInitials, joinNames, plural } from '../lib/format.ts'
import { pendingChangesCount } from '../lib/outbox.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Sheet } from './Sheet.tsx'
import { useConfirm, type ConfirmOptions } from './ConfirmSheet.tsx'
import { Icon } from './Icon.tsx'
import { Avatar, Button, Divider, Paragraph, TextCol, TextField } from './ui.tsx'

export async function confirmLogout(
  logout: () => Promise<void>,
  confirm: (options: ConfirmOptions) => Promise<boolean>,
): Promise<void> {
  const pending = pendingChangesCount()
  if (
    pending > 0 &&
    !(await confirm({
      title: 'Odhlásit se?',
      text: `V tomhle zařízení ${pending >= 2 && pending <= 4 ? 'čekají' : 'čeká'} ${plural(pending, 'neodeslaná změna', 'neodeslané změny', 'neodeslaných změn')}. Po odhlášení se ztratí.`,
      confirmLabel: 'Odhlásit se i tak',
      danger: true,
    }))
  ) {
    return
  }
  await logout()
}

export function CreateGroupForm({ onCreated, onCancel }: { onCreated(groupId: string): void; onCancel(): void }) {
  const s = useSignedIn()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setError('Napiš název skupiny')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const group = await api.groups.create(name.trim())
      await s.refresh()
      onCreated(group.id)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="stack" onSubmit={(e) => void submit(e)}>
      <TextField
        label="Název skupiny"
        placeholder="Třeba Německo a Polsko"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={60}
        autoFocus
      />
      {error && <Paragraph tone="error">{error}</Paragraph>}
      <div className="button-row">
        <Button onClick={onCancel}>Zrušit</Button>
        <Button kind="primary" type="submit" className="grow" disabled={busy}>
          Založit skupinu
        </Button>
      </div>
    </form>
  )
}

function membersText(group: Group): string {
  if (group.members.length <= 1) return 'Jen ty'
  return joinNames(group.members.map((m) => m.name))
}

export function GroupSwitcherSheet({
  open,
  onClose,
  onOpenAccount,
}: {
  open: boolean
  onClose(): void
  onOpenAccount(): void
}) {
  const { confirm, confirmSheet } = useConfirm()
  const s = useSignedIn()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  const close = () => {
    setCreating(false)
    onClose()
  }

  return (
    <>
      <Sheet open={open} onClose={close} title="Skupiny">
        <div className="stack-8">
          <Paragraph>Každá skupina má vlastní šablonu, obchody a nákupy. Všichni ve skupině vidí totéž.</Paragraph>
          <div className="group-list">
            {s.groups.map((group) => {
              const current = group.id === s.group.id
              return (
                <button
                  key={group.id}
                  type="button"
                  className="group-row"
                  aria-current={current}
                  onClick={() => {
                    s.selectGroup(group.id)
                    close()
                  }}
                >
                  <span className={`group-tile${current ? ' is-current' : ''}`}>{groupInitials(group.name)}</span>
                  <TextCol title={group.name} sub={membersText(group)} />
                  {current && <Icon name="check" size={22} strokeWidth={2.2} />}
                </button>
              )
            })}
          </div>
          <Divider />
          {creating ? (
            <CreateGroupForm
              onCancel={() => setCreating(false)}
              onCreated={(groupId) => {
                s.selectGroup(groupId)
                close()
                navigate('/skupina')
              }}
            />
          ) : (
            <button type="button" className="create-row" onClick={() => setCreating(true)}>
              <span className="create-tile">
                <Icon name="plus" size={22} />
              </span>
              Založit skupinu
            </button>
          )}
          <Divider />
          <div className="account-row">
            <button type="button" className="account-link" onClick={onOpenAccount}>
              <Avatar name={s.user.name} size={44} />
              <TextCol title={s.user.name} sub="Můj účet" />
            </button>
            <Button kind="danger" compact onClick={() => void confirmLogout(s.logout, confirm)}>
              Odhlásit se
            </Button>
          </div>
        </div>
      </Sheet>
      {confirmSheet}
    </>
  )
}
